import { CardEmpty } from 'presentation/ui/components/cards/card-empty/CardEmpty';
import { useContext, useEffect, useState } from 'react';

import { L10nContext } from 'context/L10nContext';
import { useDispatch, useSelector } from 'react-redux';

import { ChartDescription } from 'services/device/domain/business/common/description/ChartDescription';
import { getSequenceTypeDefinition } from 'services/device/domain/business/inventory/SequenceTypeDefinition';
import { RecordViewModel } from 'services/device/domain/model/RecordModel';
import { SequenceViewModel } from 'services/device/domain/model/SequenceModel';
import { SequenceChart } from 'services/device/presentation/ui/sequence-chart/SequenceChart';

import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { ButtonToggleSet } from 'presentation/ui/components/button/ButtonToggleSet';
import { ButtonToggleProps } from 'presentation/ui/partials/button/button-toggle/ButtonToggle';
import { SequencePeriodContext } from 'services/device/presentation/ui/sequence-records/context/SequencePeriodContext';
import { fetchRecordsBySequence, selectRecordsBySequenceWithPeriod } from 'services/device/store/recordSlice';
import { AsyncFetchStatus } from '../../../../../store/common/AsyncFetchStatus';
import { useTypedSelector } from '../../../../../store/common/TypedSelector';
import { ClientContext } from '../../../../core/context/ClientContext';
import { FacilityContext } from '../../../../core/context/FacilityContext';

export interface SequenceChartsProps {
	sequence: SequenceViewModel;
	showTooltip?: boolean;
	showOverlay?: boolean;
	onSelect?: (record: RecordViewModel) => void;
	onDeselect?: (record: RecordViewModel) => void;
	onDetails?: (record: RecordViewModel) => void;
}

export const SequenceCharts = (props: SequenceChartsProps): JSX.Element | null => {

	const {
		sequence,
		onSelect = null,
		onDeselect = null,
		onDetails = null,
		showOverlay = false,
		showTooltip = false
	} = props;

	const l10nContext = useContext(L10nContext);
	const clientContext = useContext(ClientContext);
	const facilityContext = useContext(FacilityContext);
	const sequencePeriodContext = useContext(SequencePeriodContext);

	// Consume the dispatch object
	const dispatch = useDispatch();

	const sequenceTypeDfinition = getSequenceTypeDefinition(sequence.Type);
	const chartDescriptions = sequenceTypeDfinition.getChartDescriptions();

	// Use a custom selector function to provide type information for the state defined in the slice
	const recordsStoreFetchStatus = useTypedSelector(state => state.records.fetchStatus);

	// Read records from the server
	useEffect(() => {
		if (recordsStoreFetchStatus === AsyncFetchStatus.INITIAL || recordsStoreFetchStatus === AsyncFetchStatus.IDLE) {
			dispatch(fetchRecordsBySequence({
				clientUuid: clientContext.selectedClientUuid,
				facilityUuid: facilityContext.selectedFacilityUuid,
				sequenceUuid: props.sequence.Uuid
			}));
		}
	});

	const [activeChart, setActiveChart] = useState<ChartDescription>(chartDescriptions?.[0] ?? null);

	// Select the records from the store
	const records = useSelector(selectRecordsBySequenceWithPeriod(sequence, sequencePeriodContext.dateStart, sequencePeriodContext.dateEnd));
	if (records.length === 0) {
		return (
			<CardEmpty message={l10nContext.translate('sequence.chart.emptyState')} />
		);
	}

	if (chartDescriptions.length === 0) {
		return null;
	}

	const selectChart = (chartName: string): void => {
		for (const chartDescription of chartDescriptions) {
			if (chartDescription.name === chartName) {
				setActiveChart(chartDescription);
				return;
			}
		}
	};

	const renderTabbar = (): JSX.Element | null => {
		if (chartDescriptions.length < 2) {
			return null;
		}
		const buttonProperties: Array<ButtonToggleProps> = [];
		for (const chartDescription of chartDescriptions) {
			buttonProperties.push({
				id: chartDescription.name,
				buttonText: l10nContext.translate(chartDescription.labelKey),
				icon: IconIdentifier.CHART
			} as ButtonToggleProps);
		}
		return (
			<ButtonToggleSet
				onClick={(id) => {
					selectChart(id);
				}}
				buttons={buttonProperties}
				deSelectAll={false}
			/>
		);
	};

	const renderCharts = (): JSX.Element => {
		return (
			<>
				{renderTabbar()}
				<SequenceChart
					key={activeChart.name}
					sequence={sequence}
					chartDescription={activeChart}
					onSelect={onSelect}
					onDeselect={onDeselect}
					onDetails={onDetails}
					showOverlay={showOverlay}
					showTooltip={showTooltip}
				/>
			</>
		);
	};

	return (
		<div className="sequence-charts">
			{renderCharts()}
		</div>
	);

};
